import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wand2, Check, Palette, RotateCcw } from "lucide-react";
import api, { apiErr } from "@/lib/api";

const SAMPLES = [
  "Cyberpunk neon fashion store with dark glass panels and electric highlights",
  "Warm, earthy organic wellness brand with soft greens and cream tones",
  "Premium minimalist electronics store, deep navy with crisp white",
  "Playful candy shop, bright pastels and rounded friendly type",
];

const HEX = /^#[0-9a-fA-F]{6}$/;
const LABELS = { primary: "Primary", accent: "Accent", bg: "Background", surface: "Surface", text: "Text", muted: "Muted" };
const ORDER = Object.keys(LABELS);

export default function AITheme() {
  const [prompt, setPrompt] = useState("");
  const [promptErr, setPromptErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(null);
  const [store, setStore] = useState(null);
  const [applying, setApplying] = useState(false);
  const [original, setOriginal] = useState(null);   // the palette the store currently uses (for "Reset")
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    api.get("/vendor/store").then(({ data }) => {
      // an older primary / secondary override is shown as part of the palette so what you see is what the store uses
      const own = data.theme_colors || {};
      const t = { ...data.theme, colors: { ...data.theme?.colors, ...(own.primary && { primary: own.primary }), ...(own.accent && { accent: own.accent }) } };
      setStore(data); setTheme(t); setOriginal(t.colors);
    });
    api.get("/themes").then(({ data }) => setCatalog(data)).catch(() => {});
  }, []);

  const setColors = (colors) => setTheme((t) => ({ ...t, colors: { ...colors } }));
  const setColor = (k, v) => setTheme((t) => ({ ...t, colors: { ...t.colors, [k]: v } }));
  const colorsOk = theme && ORDER.filter((k) => k in theme.colors).every((k) => HEX.test(theme.colors[k]));
  const dirty = theme && original && ORDER.some((k) => (theme.colors[k] || "").toLowerCase() !== (original[k] || "").toLowerCase());
  const base = theme && catalog.find((t) => t.id === theme.id);

  const generate = async () => {
    const p = prompt.trim();
    if (!p) { setPromptErr("Describe your store to generate a theme."); return toast.error("Describe your store to generate a theme."); }
    if (p.length < 10) { setPromptErr("Add a bit more detail (at least 10 characters)."); return toast.error("Add a bit more detail (at least 10 characters)."); }
    setLoading(true);
    try {
      const { data } = await api.post("/vendor/ai-theme", { prompt });
      setTheme(data.theme);
      toast.success("Theme generated! Preview it below.");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const apply = async () => {
    if (!colorsOk) return toast.error("Every colour must be a hex value like #1d4ed8.");
    setApplying(true);
    try {
      const t = { ...theme, colors: Object.fromEntries(Object.entries(theme.colors).map(([k, v]) => [k, v.toLowerCase()])) };
      await api.put("/vendor/store/theme", t);
      setTheme(t); setOriginal(t.colors);
      toast.success("Colours applied to your storefront!");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  const locked = store && !store.plan_info?.ai_theme;

  return (
    <div className="space-y-4" data-testid="vendor-ai-theme">
      <div>
        <h1 className="font-display text-[20px] font-bold text-neutral-900">AI Theme Generator</h1>
        <p className="text-slate-500 text-[13px]">Describe your dream store and let AI craft a matching color & font theme.</p>
      </div>

      {locked && (
        <div className="rounded-xl bg-orange-50 border border-orange-200 p-4 text-sm text-orange-600">
          The AI Theme Generator is not included in your current plan. <a href="/vendor/plan" className="underline font-semibold">Upgrade your plan</a> to unlock it. You can still set your store's colours by hand in the preview.
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-5">
          <label className="block">
            <span className="block text-[12px] font-semibold text-slate-700 mb-1">Describe your store <span className="text-red-500">*</span></span>
            <textarea value={prompt} onChange={(e) => { setPrompt(e.target.value); setPromptErr(""); }} rows={3} disabled={locked} data-testid="vendor-ai-prompt-input"
              placeholder="e.g. Bold streetwear brand with black backgrounds and neon orange accents"
              maxLength={300} aria-invalid={promptErr ? "true" : "false"} className={`w-full rounded-lg bg-white border px-3 py-2 text-[13px] text-neutral-900 focus:ring-2 outline-none transition disabled:opacity-50 ${promptErr ? "border-red-500 focus:border-red-500 focus:ring-red-500/15" : "border-slate-300 focus:border-blue-600 focus:ring-blue-600/15"}`} />
          </label>
          {promptErr && <p role="alert" className="mt-1 text-[11px] text-red-600">{promptErr}</p>}
          <div className="text-[11px] text-slate-500 mt-1 text-right">{prompt.length}/300</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {SAMPLES.map((s) => (
              <button key={s} onClick={() => setPrompt(s)} disabled={locked}
                className="text-[11px] px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-500 hover:text-blue-600 hover:border-orange-200 transition-colors disabled:opacity-50">
                {s.slice(0, 32)}…
              </button>
            ))}
          </div>
          <button onClick={generate} disabled={loading || locked} data-testid="vendor-ai-generate-theme-btn"
            className="mt-4 w-full h-9 flex items-center justify-center gap-2 rounded-lg text-[13px] font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-60">
            <Wand2 size={15} /> {loading ? "Generating…" : "Generate theme"}
          </button>
        </div>

        <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4"><Palette size={18} className="text-orange-600" /><h2 className="font-display font-semibold">Live preview</h2></div>
          {theme ? (
            <div>
              <div className="rounded-xl overflow-hidden border border-slate-200" style={{ background: theme.colors.bg }}>
                <div className="p-4" style={{ background: theme.colors.primary }}>
                  <span className="font-bold" style={{ color: "#fff", fontFamily: theme.font }}>{store?.name || theme.name || "My Store"}</span>
                </div>
                <div className="p-5" style={{ color: theme.colors.text, fontFamily: theme.font }}>
                  <div className="text-lg font-bold">Featured product</div>
                  <div className="rounded-lg mt-3 p-4" style={{ background: theme.colors.surface }}>
                    <div className="h-20 rounded" style={{ background: theme.colors.primary, opacity: 0.2 }} />
                    <div className="mt-2 text-sm">Premium Item</div>
                    <div className="text-[12px]" style={{ color: theme.colors.muted }}>Short product description in muted text</div>
                    <button className="mt-2 px-4 py-1.5 rounded-full text-sm font-bold" style={{ background: theme.colors.accent, color: "#0b0b0b" }}>Add to cart</button>
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-4 mb-2">Click a colour to change it, or type a hex value. The preview updates as you edit.</p>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="ai-theme-colors">
                {ORDER.filter((k) => k in theme.colors).map((k) => {
                  const v = theme.colors[k], ok = HEX.test(v);
                  return (
                    <div key={k} className="text-center">
                      <label className="block relative h-9 rounded border border-slate-300 cursor-pointer overflow-hidden" style={{ background: ok ? v : "#fff" }} title={`Change ${LABELS[k]}`}>
                        <input type="color" value={ok ? v : "#000000"} onChange={(e) => setColor(k, e.target.value)} data-testid={`ai-theme-${k}-picker`} aria-label={`${LABELS[k]} colour`} className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
                      </label>
                      <div className="text-[10px] text-slate-500 mt-1 font-mono">{k}</div>
                      <input value={v} maxLength={7} spellCheck={false} onChange={(e) => setColor(k, e.target.value.startsWith("#") ? e.target.value : `#${e.target.value}`)} data-testid={`ai-theme-${k}-hex`} aria-label={`${LABELS[k]} hex`} aria-invalid={!ok}
                        className={`mt-0.5 w-full h-7 rounded border px-1 text-center font-mono text-[11px] uppercase outline-none focus:ring-2 ${ok ? "border-slate-200 focus:ring-blue-600/15 focus:border-blue-600" : "border-red-500 focus:ring-red-500/15"}`} />
                    </div>
                  );
                })}
              </div>
              {!colorsOk && <p role="alert" className="mt-1.5 text-[11px] text-red-600">Enter each colour as a hex value like #1d4ed8.</p>}
              <div className="flex gap-3 mt-2 text-[11px]">
                {dirty && <button onClick={() => setTheme((t) => ({ ...t, colors: original }))} className="text-slate-500 hover:text-blue-600 inline-flex items-center gap-1" data-testid="ai-theme-undo"><RotateCcw size={11} /> Undo changes</button>}
                {base && ORDER.some((k) => (theme.colors[k] || "").toLowerCase() !== (base.colors[k] || "").toLowerCase()) && (
                  <button onClick={() => setColors(base.colors)} className="text-slate-500 hover:text-blue-600 inline-flex items-center gap-1" data-testid="ai-theme-default"><RotateCcw size={11} /> Use {base.name}'s default colours</button>
                )}
              </div>
              <button onClick={apply} disabled={applying || !colorsOk} data-testid="vendor-ai-apply-theme"
                className="mt-4 w-full h-9 flex items-center justify-center gap-2 rounded-lg text-[13px] font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-60">
                <Check size={15} /> {applying ? "Applying…" : "Apply to storefront"}
              </button>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-500 text-sm">Generate a theme to see the preview</div>
          )}
        </div>
      </div>
    </div>
  );
}
